// N3 (2026-05-08, ALGORITHM_FLOW v0.49) — BoxHero 연동 패널 정정.
//
//   본질 design = 2 layer 분리. 옛 8 정책 (clear-mappings / wipe-ingredients / import-all-as-ingredients) = 모두 폐기.
//
//   N3 (2026-05-08) 변경:
//     - 기본 출고 위치 section 폐기 (= 사용자 dictation "기본 출고 위치 = 이건 뭔데 여기 생긴거냐?")
//     - webhook status badge 추가 (= 본 phase 영역 영역 자동 등록 결과 surface)
//     - 매핑 카운트 (재료 ↔ BoxHero) section 보존
//
//   보존 영역 = token register / revoke / status / resync.
//
// RBAC: 모든 mutation owner-only.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  boxheroIntegrationControllerGetStatus,
  boxheroIntegrationControllerRegisterToken,
  boxheroIntegrationControllerResync,
  boxheroIntegrationControllerRevokeToken,
} from '@dvvb/sdk';

import {
  AlertBanner,
  Badge,
  Button,
  FormField,
  Icon,
  TextInput,
  useToast,
} from '../../components/primitives';
import { ApiCallError, unwrap } from '../../lib/envelope';
import { useAuth } from '../../auth/AuthContext';
import { queryClient } from '../../lib/queryClient';
import type { BoxheroStatusResponse, BoxheroSyncStatus } from '../../types/api';

const SYNC_LABEL: Record<BoxheroSyncStatus, string> = {
  active: '활성',
  paused: '일시중지',
  error: '오류',
  unknown: '미상',
};

const SYNC_VARIANT: Record<BoxheroSyncStatus, 'success' | 'warning' | 'danger' | 'default'> = {
  active: 'success',
  paused: 'warning',
  error: 'danger',
  unknown: 'default',
};

// N3 (2026-05-08) — webhook status 매핑.
function webhookLabel(s: string): string {
  switch (s) {
    case 'active':
      return '활성';
    case 'manual_required':
      return '수동 등록 필요';
    case 'error':
      return '오류';
    default:
      return '미상';
  }
}
function webhookVariant(s: string): 'success' | 'warning' | 'danger' | 'default' {
  switch (s) {
    case 'active':
      return 'success';
    case 'manual_required':
      return 'warning';
    case 'error':
      return 'danger';
    default:
      return 'default';
  }
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

export function SettingsIntegrationsRoute() {
  const auth = useAuth();
  const toast = useToast();
  const isOwner = auth.role === 'owner';

  const [token, setToken] = useState('');

  const statusQ = useQuery({
    queryKey: ['boxhero', 'status'],
    queryFn: async () => {
      const r = await boxheroIntegrationControllerGetStatus();
      return unwrap<BoxheroStatusResponse>(r);
    },
  });

  const registerMu = useMutation({
    mutationFn: async (t: string) => {
      const r = await boxheroIntegrationControllerRegisterToken({
        body: { token: t } as never,
      });
      return unwrap(r);
    },
    onSuccess: async () => {
      setToken('');
      await queryClient.invalidateQueries({ queryKey: ['boxhero', 'status'] });
      toast.success('BoxHero 토큰을 등록했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '토큰 등록 실패');
    },
  });

  const revokeMu = useMutation({
    mutationFn: async () => {
      const r = await boxheroIntegrationControllerRevokeToken();
      return unwrap<{ revoked: boolean; mirrorsCleared: number }>(r);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['boxhero', 'status'] });
      await queryClient.invalidateQueries({ queryKey: ['inventory'] });
      toast.success(`연동 해제 완료 — 캐시 ${data.mirrorsCleared}건 정리`);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '해제 실패');
    },
  });

  const resyncMu = useMutation({
    mutationFn: async () => {
      const r = await boxheroIntegrationControllerResync();
      return unwrap<{ enqueued: boolean }>(r);
    },
    onSuccess: () => {
      toast.success('재동기화 작업이 큐에 추가되었습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '재동기화 실패');
    },
  });

  const status = statusQ.data;
  const connected = status?.connected ?? false;
  const syncStatus = (status?.syncStatus ?? 'unknown') as BoxheroSyncStatus;
  const webhookStatus = status?.webhookStatus ?? 'unknown';

  if (statusQ.isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        BoxHero 상태를 불러오는 중…
      </div>
    );
  }
  if (statusQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`상태 조회 실패: ${(statusQ.error as ApiCallError).message ?? '오류'}`}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Status card */}
      <div
        style={{
          padding: 16,
          border: '1px solid var(--border-base)',
          borderRadius: 8,
          background: 'var(--bg-surface)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700 }}>BoxHero 연동</div>
          <Badge variant={connected ? 'success' : 'default'}>
            {connected ? '연결됨' : '미연결'}
          </Badge>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
          <div>
            <div style={{ color: 'var(--fg-3)' }}>팀명</div>
            <div>{status?.teamName ?? '—'}</div>
          </div>
          <div>
            <div style={{ color: 'var(--fg-3)' }}>등록일</div>
            <div>{fmtDate(status?.registeredAt)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--fg-3)' }}>마지막 동기화</div>
            <div>{fmtDate(status?.lastSyncAt)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--fg-3)' }}>동기화 상태</div>
            <Badge variant={SYNC_VARIANT[syncStatus]}>{SYNC_LABEL[syncStatus]}</Badge>
          </div>
          <div>
            <div style={{ color: 'var(--fg-3)' }}>매핑 카운트 (재료 ↔ BoxHero)</div>
            <div>{status?.mappings?.item ?? 0}</div>
          </div>
          {/* N3 (2026-05-08) — webhook 자동 등록 결과 surface */}
          <div>
            <div style={{ color: 'var(--fg-3)' }}>Webhook (실시간 sync)</div>
            <Badge variant={webhookVariant(webhookStatus)}>{webhookLabel(webhookStatus)}</Badge>
          </div>
        </div>
        {webhookStatus === 'manual_required' && (
          <div style={{ marginTop: 12 }}>
            <AlertBanner
              tone="warning"
              message={`webhook 자동 등록이 지원되지 않아 BoxHero 화면에서 수동 등록이 필요합니다. 등록 URL: ${status?.webhookUrl ?? '(미설정)'}`}
            />
          </div>
        )}
        {status?.lastError && (
          <div style={{ marginTop: 12 }}>
            <AlertBanner tone="danger" message={`마지막 오류: ${status.lastError}`} />
          </div>
        )}
      </div>

      {/* Token register */}
      {!connected && (
        <div
          style={{
            padding: 16,
            border: '1px solid var(--border-base)',
            borderRadius: 8,
            background: 'var(--bg-surface)',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
            토큰 등록
          </div>
          <FormField label="BoxHero API 토큰">
            <TextInput
              type="password"
              value={token}
              onChange={setToken}
              placeholder="bxh_..."
              disabled={!isOwner || registerMu.isPending}
            />
          </FormField>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Button
              variant="strong"
              disabled={!isOwner || !token.trim() || registerMu.isPending}
              onClick={() => registerMu.mutate(token.trim())}
            >
              {registerMu.isPending ? '등록 중...' : '등록'}
            </Button>
          </div>
        </div>
      )}

      {/* Connected actions — N3 (2026-05-08) — 기본 출고 위치 section 폐기 */}
      {connected && (
        <div
          style={{
            padding: 16,
            border: '1px solid var(--border-base)',
            borderRadius: 8,
            background: 'var(--bg-surface)',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>액션</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              disabled={!isOwner || resyncMu.isPending}
              onClick={() => resyncMu.mutate()}
            >
              <Icon name="refresh-cw" size={14} /> 재동기화
            </Button>
            <Button
              variant="secondary"
              disabled={!isOwner || revokeMu.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'BoxHero 연동을 해제할까요?\n\n캐시 데이터(현재재고)가 정리됩니다. 같은 토큰으로 재연결 시 복구됩니다.\n재료(inventory)는 보존됩니다.',
                  )
                ) {
                  revokeMu.mutate();
                }
              }}
              style={{ color: 'var(--danger)' }}
            >
              {revokeMu.isPending ? '해제 중...' : '연동 해제'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
